import { lazy, Suspense, useEffect, useMemo, useRef } from 'react';

import { useMaintenanceTools, useMyTabs } from '../../hooks/useAdmin';
import { useIngestHealth } from '../../hooks/useCrmAdmin';
import { useEnrichmentAccess } from '../../hooks/useCrmEnrichment';
import { useViewParam } from '../../hooks/navigation/useUrlState';
import { navigateTab } from '../../hooks/useTabRouter';
import styles from './AdminPage.module.css';
import { AdminNav, type AdminNavGroup, type AdminNavItem } from '../admin/AdminNav';
import {
  RECENT_RUNS_SECTION_ID,
  toolGroupSections,
} from '../admin/toolRunner/toolGroups';

const RosterAccessAdminGroup = lazy(() => import('../admin/groups/RosterAccessAdminGroup'));
const JobsAdminGroup = lazy(() => import('../admin/groups/JobsAdminGroup'));
const CrmDashboardAdminGroup = lazy(() => import('../admin/groups/CrmDashboardAdminGroup'));
const GovernanceAdminGroup = lazy(() => import('../admin/groups/GovernanceAdminGroup'));
const MonitoringAdminGroup = lazy(() => import('../admin/groups/MonitoringAdminGroup'));
const EnrichmentAdminGroup = lazy(() => import('../admin/groups/EnrichmentAdminGroup'));
const MaintenanceAdminGroup = lazy(() => import('../admin/groups/MaintenanceAdminGroup'));
const ToolsAdminGroup = lazy(() => import('../admin/groups/ToolsAdminGroup'));
const ReportsAdminGroup = lazy(() => import('../admin/groups/ReportsAdminGroup'));

const DEFAULT_ADMIN_GROUP = 'roster-access';

const ADMIN_ONLY_GROUPS = new Set([
  'roster-access',
  'jobs',
  'crm-dashboard',
  'monitoring',
  'reports',
  'maintenance',
  'tools',
]);

const LEGACY_SECTION_GROUPS: Record<string, string> = {
  'admin-roster': 'roster-access',
  'admin-notifications': 'roster-access',
  'admin-tab-visibility': 'roster-access',
  'admin-impersonation': 'roster-access',
  'admin-schedules': 'jobs',
  'admin-schedule-runs': 'jobs',
  'crm-privacy': 'crm-dashboard',
  'admin-fund-settings': 'crm-dashboard',
  'crm-search': 'crm-dashboard',
  'admin-deal-aliases': 'crm-dashboard',
  'gov-groups': 'governance',
  'gov-protections': 'governance',
  'gov-clearances': 'governance',
  'gov-mp': 'governance',
  'gov-buckets': 'governance',
  'gov-sharing-groups': 'governance',
  'gov-audit': 'governance',
  'crm-ingest-health': 'monitoring',
  'admin-llm-spend': 'monitoring',
  'admin-fact-proposals': 'reports',
  'crm-enrichment': 'enrichment',
  'crm-notes-import': 'maintenance',
  'crm-backload': 'maintenance',
  'crm-entity-backload': 'maintenance',
  'admin-maintenance': 'maintenance',
};

function legacyGroupFromHash(): string | null {
  const sectionId = window.location.hash.replace(/^#/, '');
  return LEGACY_SECTION_GROUPS[sectionId] ?? null;
}

export function AdminPage() {
  const [urlGroup] = useViewParam('group', DEFAULT_ADMIN_GROUP);
  const hasExplicitGroup = new URLSearchParams(window.location.search).has('group');
  const requestedGroup = hasExplicitGroup ? urlGroup : (legacyGroupFromHash() ?? urlGroup);
  // CRM governance capability (ACTUAL identity → survives view-as). Gates the
  // Admin-tab Governance group (W1-PR4e-D2). Decoupled from is_admin.
  const myTabs = useMyTabs();
  const isGovAdmin = myTabs.data?.capabilities?.is_governance_admin ?? false;
  // Separation of duties: a pure governance admin (is_governance_admin, NOT is_admin) reaches this
  // tab (tabs.py opens it) but must see ONLY the Governance group — every IT surface (CRM Admin +
  // Admin Sections, nav + panels) is gated on is_admin. Fails closed while caps load.
  const isAdmin = myTabs.data?.capabilities?.is_admin ?? false;
  const enrichmentAccess = useEnrichmentAccess();
  const enrichPilot = enrichmentAccess.data?.pilot === true;
  const fallbackGroup = isAdmin ? DEFAULT_ADMIN_GROUP : isGovAdmin ? 'governance' : '';
  const requestedAllowed = requestedGroup === 'governance'
    ? isGovAdmin
    : requestedGroup === 'enrichment'
      ? isAdmin && enrichPilot
      : isAdmin && ADMIN_ONLY_GROUPS.has(requestedGroup);
  const activeGroupId = requestedAllowed ? requestedGroup : fallbackGroup;

  // Group-local queries are disabled until their group is selected. Once a
  // group has been visited, React Query may still serve its cached badge data,
  // but it does not keep polling while the group is inactive.
  const ingestHealth = useIngestHealth(activeGroupId === 'monitoring');
  const deadLetter = ingestHealth.data?.queue.dead_letter ?? null;

  // The Tools group's sections ARE its nav items: one `tool.group` string from the
  // registry = one on-page section = one link, so a group added in a backend PR grows
  // both with no frontend change. Shares ToolsAdminGroup's query key, so the sidebar and
  // the page are one fetch. Until it resolves there are no items — AdminNav renders items
  // only for the active group, and the group-redirect effect below tolerates an undefined
  // first section.
  const toolsQuery = useMaintenanceTools(activeGroupId === 'tools');
  const toolNavItems = useMemo<AdminNavItem[]>(() => {
    const sections = toolGroupSections(toolsQuery.data?.tools ?? []);
    if (sections.length === 0) return [];
    return [
      ...sections.map((section) => ({ id: section.slug, label: section.name })),
      { id: RECENT_RUNS_SECTION_ID, label: 'Recent runs' },
    ];
  }, [toolsQuery.data]);

  const navGroups = useMemo<AdminNavGroup[]>(() => {
    const groups: AdminNavGroup[] = [];
    if (isAdmin) {
      groups.push({
        id: 'roster-access',
        heading: 'Roster, Access & Notifications',
        items: [
          { id: 'admin-roster', label: 'Team Roster' },
          { id: 'admin-notifications', label: 'Notifications' },
          { id: 'admin-tab-visibility', label: 'Tab Visibility' },
          { id: 'admin-impersonation', label: 'View as user' },
        ],
      });
      groups.push({
        id: 'jobs',
        heading: 'Jobs',
        items: [
          { id: 'admin-schedules', label: 'Schedules' },
          { id: 'admin-schedule-runs', label: 'Recent runs' },
        ],
      });
      groups.push({
        id: 'crm-dashboard',
        heading: 'CRM & Dashboard',
        items: [
          { id: 'crm-privacy', label: 'Privacy' },
          { id: 'admin-fund-settings', label: 'Fund Settings' },
          { id: 'crm-search', label: 'Search' },
          { id: 'admin-deal-aliases', label: 'Deal Aliases' },
          // Portco master record (portco-transactions S0.1) — READ-ONLY over a DARK table. Renders
          // empty until an operator runs the seed script; the panel is how ruling R4's company list
          // gets confirmed without a database query.
          { id: 'admin-portfolio-companies', label: 'Portfolio Companies' },
        ],
      });
    }
    if (isGovAdmin) {
      groups.push({
        id: 'governance',
        heading: 'Governance',
        items: [
          { id: 'gov-groups', label: 'Groups' },
          { id: 'gov-protections', label: 'Protections' },
          { id: 'gov-clearances', label: 'Clearances' },
          { id: 'gov-mp', label: 'Managing partners' },
          { id: 'gov-buckets', label: 'Default visibility' },
          { id: 'gov-sharing-groups', label: 'Sharing groups' },
          { id: 'gov-audit', label: 'Access audit' },
        ],
      });
    }
    if (isAdmin) {
      groups.push({
        id: 'monitoring',
        heading: 'Monitoring',
        items: [
          { id: 'crm-ingest-health', label: 'Ingest Health', badge: deadLetter },
          { id: 'admin-llm-spend', label: 'LLM Spend' },
        ],
      });
      groups.push({
        id: 'reports',
        heading: 'Reports',
        items: [
          { id: 'admin-fact-proposals', label: 'Fact Proposals' },
        ],
      });
      if (enrichPilot) {
        groups.push({
          id: 'enrichment',
          heading: 'Enrichment',
          items: [{ id: 'crm-enrichment', label: 'Enrichment Operations' }],
        });
      }
      groups.push({
        id: 'maintenance',
        heading: 'Maintenance',
        items: [
          { id: 'admin-maintenance', label: 'Operations tools' },
          { id: 'crm-notes-import', label: 'Import notes' },
          { id: 'crm-backload', label: 'Backload jobs' },
          { id: 'crm-entity-backload', label: 'Create records' },
        ],
      });
      groups.push({
        id: 'tools',
        heading: 'Tools',
        items: toolNavItems,
      });
    }
    return groups;
  }, [deadLetter, isGovAdmin, isAdmin, enrichPilot, toolNavItems]);

  const navigationReady = myTabs.isSuccess && (!isAdmin || enrichmentAccess.isFetched);
  useEffect(() => {
    if (!navigationReady || !activeGroupId || requestedGroup === activeGroupId) return;
    const firstSection = navGroups.find((group) => group.id === activeGroupId)?.items[0]?.id;
    navigateTab('admin', {
      params: activeGroupId === DEFAULT_ADMIN_GROUP ? undefined : { group: activeGroupId },
      hash: firstSection,
      replace: true,
    });
  }, [activeGroupId, navGroups, navigationReady, requestedGroup]);

  // Scroll-on-arrival, Tools only. Every other group's sections are in the DOM by the
  // time the browser handles the hash, so they get native hash scrolling for free; a
  // Tools section does not exist until the registry fetch resolves, long after the
  // browser has stopped caring. Runs once per arrival — the ref remembers the hash it
  // honoured and resets when the group changes, so this cannot yank the page back while
  // an operator is reading further down.
  const scrolledToHash = useRef<string | null>(null);
  useEffect(() => {
    if (activeGroupId !== 'tools') {
      scrolledToHash.current = null;
      return;
    }
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash || scrolledToHash.current === hash) return;
    if (!toolNavItems.some((item) => item.id === hash)) return;
    scrolledToHash.current = hash;
    // The section can be one frame behind: the lazy chunk and the registry fetch resolve
    // independently, so scroll after the paint that puts it in the DOM.
    const frame = requestAnimationFrame(() => {
      document.getElementById(hash)?.scrollIntoView();
    });
    return () => cancelAnimationFrame(frame);
  }, [activeGroupId, toolNavItems]);

  const navigateGroup = (groupId: string) => {
    const firstSection = navGroups.find((group) => group.id === groupId)?.items[0]?.id;
    navigateTab('admin', {
      params: groupId === DEFAULT_ADMIN_GROUP ? undefined : { group: groupId },
      hash: firstSection,
    });
  };

  const navigateSection = (sectionId: string) => {
    navigateTab('admin', {
      params: activeGroupId === DEFAULT_ADMIN_GROUP ? undefined : { group: activeGroupId },
      hash: sectionId,
      replace: true,
    });
  };

  return (
    <div className={styles.root}>
      <AdminNav
        groups={navGroups}
        activeGroupId={activeGroupId}
        onGroupChange={navigateGroup}
        onSectionChange={navigateSection}
      />
      <div className={styles.content}>
        <Suspense fallback={<div className={styles.groupLoading}>Loading admin group…</div>}>
          {isAdmin && activeGroupId === 'roster-access' && <RosterAccessAdminGroup />}
          {isAdmin && activeGroupId === 'jobs' && <JobsAdminGroup />}
          {isAdmin && activeGroupId === 'crm-dashboard' && <CrmDashboardAdminGroup />}
          {isGovAdmin && activeGroupId === 'governance' && <GovernanceAdminGroup />}
          {isAdmin && activeGroupId === 'monitoring' && <MonitoringAdminGroup />}
          {isAdmin && activeGroupId === 'reports' && <ReportsAdminGroup />}
          {isAdmin && activeGroupId === 'enrichment' && <EnrichmentAdminGroup />}
          {isAdmin && activeGroupId === 'maintenance' && <MaintenanceAdminGroup />}
          {isAdmin && activeGroupId === 'tools' && <ToolsAdminGroup />}
        </Suspense>
      </div>
    </div>
  );
}
